import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import VerificationInput from 'react-verification-input';
import { Input, Form } from 'antd';

import { Icon as CroplerIcon, Button } from '@cropler/ui-kit';

import styles from '../../styles/auth.module.scss';

import { useResetPasswordMutation } from '../../store/api/auth';
import { getPasswordRule } from '../../utils/validation';
import { AuthTitle } from '../../components';
import { RootState } from '../../store';
import { ApiFieldError } from '../../types';

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const tokenUrl = searchParams.get('token');
  const usernameUrl = searchParams.get('username');

  const [resetPassword, { isSuccess, isLoading, isError, error, data }] = useResetPasswordMutation();
  const { username } = useSelector((state: RootState) => state.auth);

  const [form] = Form.useForm();

  const [token, setToken] = useState('');
  const [currentUsername, setCurrentUsername] = useState('');

  useEffect(() => {
    if (!usernameUrl && !username) {
      navigate('/login');
    }
    if (tokenUrl) {
      setToken(tokenUrl);
    }
    setCurrentUsername(usernameUrl ? usernameUrl : username);
  }, [tokenUrl, usernameUrl, username]);

  useEffect(() => {
    if (isSuccess) {
      navigate('/sensors');
    }
  }, [isSuccess, navigate, data, dispatch, username]);

  useEffect(() => {
    if (isError) {
      const [{ detail }] = error as ApiFieldError[];
      const confirmPassword = form.getFieldValue('resetConfirmPassword');
      form.setFields([{ name: 'resetConfirmPassword', value: confirmPassword, errors: [detail || t('common.smthWrong')] }]);
    }
  }, [isError, form, error, t]);

  const onSubmit = () => {
    form
      .validateFields()
      .then(() => {
        const password = form.getFieldValue('resetPassword');
        const confirmPassword = form.getFieldValue('resetConfirmPassword');

        if (password !== confirmPassword) {
          form.setFields([
            {
              name: 'resetConfirmPassword',
              value: confirmPassword,
              errors: [t('forms.validation.passwordDoNotMatch')],
            },
          ]);
          return;
        }

        resetPassword({ password, username: currentUsername, token });
      })
      .catch(() => {});
  };

  const getTokenFromForm = (value: string) => {
    setToken(value);
  };

  return (
    <div className={styles.formContainer}>
      <div className={styles.form}>
        <AuthTitle title={t('login.titleSetPassword')} withBackButtonOnlyMobile={true} />
        <p className={styles.descriptionPassword}>{t('password.text')}</p>
        <ul className={styles.list}>
          <li className={styles.listItem}>{t('password.condition1')}</li>
          <li className={styles.listItem}>{t('password.condition2')}</li>
        </ul>
        <Form
          form={form}
          initialValues={{ password: '', confirmPassword: '' }}
          autoComplete="off"
          className={styles.formPassword}
        >
          <Form.Item name="resetPassword" rules={[getPasswordRule(t)]}>
            <Input.Password
              prefix={<CroplerIcon name="lock" />}
              placeholder={t('labels.enterNewPassword')}
              size="large"
            />
          </Form.Item>
          <Form.Item name="resetConfirmPassword">
            <Input.Password
              prefix={<CroplerIcon name="lock" />}
              placeholder={t('labels.confirmPassword')}
              size="large"
            />
          </Form.Item>

          {!tokenUrl && (
            <div className={styles.formVerification}>
              <div className={styles.formVerificationLabel}>{t('login.enterTokenMessage')}</div>
              <VerificationInput
                onComplete={(value) => getTokenFromForm(value)}
                validChars="0-9"
                inputProps={{ inputMode: 'numeric' }}
                placeholder=""
              />
            </div>
          )}
        </Form>
        <Button className={styles.buttonSubmit} type="primary" onClick={onSubmit} disabled={isLoading} size={'large'}>
          {t('actions.setPassword')}
        </Button>
      </div>
    </div>
  );
};

export default ResetPassword;
